'use client';

import React, { useState, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ArrowsRightLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import CreateArticleDialog, { CreateArticleDialogRef } from './CreateArticleDialog';
import { ArticlePlanningData, ManualModeStep } from './ManualMode';
import AIArticleSuggestions, { AIGeneratedArticle } from './AIArticleSuggestions';
import { articleAiApi } from '@/app/api/article-ai';
import { toast } from 'react-hot-toast';

interface ArticleWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (article: AIGeneratedArticle, formData: ArticlePlanningData) => void;
  initialFormData?: ArticlePlanningData;
  onFormDataChange?: (formData: ArticlePlanningData) => void;
  showCreateFromBlank?: boolean;
}

type WizardStep = 'planning' | 'suggestions';

export default function ArticleWizardModal({
  isOpen,
  onClose,
  onComplete,
  initialFormData,
  onFormDataChange,
  showCreateFromBlank = true,
}: ArticleWizardModalProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('planning');
  const [manualModeStep, setManualModeStep] = useState<ManualModeStep>('purpose');
  const [mode, setMode] = useState<'chat' | 'manual'>('chat');
  const [planningData, setPlanningData] = useState<ArticlePlanningData | null>(null);
  const [generatedArticle, setGeneratedArticle] = useState<AIGeneratedArticle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const dialogRef = useRef<CreateArticleDialogRef>(null);

  const handleCreateFromBlank = () => {
    window.open('/articles/create?start=blank', '_blank');
    onClose();
  };

  // Handle form data changes and save them for persistence
  const handleFormDataChange = (data: ArticlePlanningData) => {
    // Save form data as user types/selects options
    setPlanningData(data);

    // Pass the form data changes to the parent component if the callback exists
    if (onFormDataChange) {
      onFormDataChange(data);
    }
  };

  // Handle completion of the planning form
  const handleManualStepChange = async (direction: 'next' | 'back') => {
    if (direction === 'back') {
      setManualModeStep('purpose');
      return;
    }

    if (manualModeStep === 'purpose') {
      const isValid = await dialogRef.current?.triggerValidation();
      if (isValid) {
        setManualModeStep('style');
      }
    }
  };

  const handlePlanningComplete = (data: ArticlePlanningData | any) => {
    // Normalize data from chat mode to match manual mode structure
    const planningDataForApi = mode === 'chat' ? {
      purpose: data.purpose,
      targetAudience: data.audience,
      intent: data.purpose, // Per schema, intent can be the same as purpose for now
      desiredLength: data.desiredLength,
      tone: data.tone,
      tags: data.tags,
      category: data.category,
    } : data;

    setPlanningData(planningDataForApi);
    setIsLoading(true);

    toast.loading('Generating article suggestions...', { id: 'generating' });

    articleAiApi.generateArticle(planningDataForApi)
      .then(article => {
        if (!article || !article.titles || !article.outline) throw new Error('Received invalid article structure from API');

        // Ensure a title is selected
        if (!article.selectedTitle && article.titles.length > 0) {
          article.selectedTitle = article.titles[0];
        }

        setGeneratedArticle(article);
        setCurrentStep('suggestions');
        toast.success('Article suggestions generated!', { id: 'generating' });

        // Don't auto-complete - wait for user to click "Accept & Continue"
        // The user needs to explicitly accept the generated content
      })
      .catch(error => {
        console.error('Error generating article:', error);

        let errorMessage = 'Failed to generate article suggestions. Please try again.';
        if (error.response) {
          // Handle specific HTTP error codes
          switch (error.response.status) {
            case 401:
              errorMessage = 'Authentication error. Please sign in again and retry.';
              break;
            case 403:
              errorMessage = 'You don\'t have permission to use this feature.';
              break;
            case 429:
              errorMessage = 'Too many requests. Please wait a moment and try again.';
              break;
            case 500:
              errorMessage = 'Server error. Our team has been notified.';
              break;
            case 404:
              errorMessage = 'API endpoint not found. Please check the configuration.';
              break;
            default:
              if (error.response.data?.error) {
                errorMessage = `Error: ${error.response.data.error}`;
              }
          }
        } else if (error.message) {
          console.error(error);
          errorMessage = 'Failed to generate article suggestions. Please try again.';
        }

        toast.error(errorMessage, { id: 'generating', duration: 5000 });

        // Reset the generating state in the planning form
        if (dialogRef.current) {
          dialogRef.current.resetManualModeGeneratingState();
        }

        // Stay on the planning form so the user can retry
        setCurrentStep('planning');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Handle title selection in the suggestions view
  const handleTitleSelect = (title: string) => {
    if (!generatedArticle) return;
    setGeneratedArticle({
      ...generatedArticle,
      selectedTitle: title
    });
  };

  // Handle outline editing in the suggestions view
  const handleOutlineEdit = (outline: AIGeneratedArticle['outline']) => {
    if (!generatedArticle) return;
    setGeneratedArticle({
      ...generatedArticle,
      outline
    });
  };

  // Handle regeneration request
  const handleRegenerateRequest = () => {
    if (!planningData) return;

    setIsLoading(true);
    toast.loading('Regenerating article suggestions...', { id: 'regenerating' });

    articleAiApi.regenerateArticle(planningData)
      .then(article => {
        // Check if we received a valid article structure
        if (!article || !article.titles || !article.outline) {
          throw new Error('Received invalid article structure from API');
        }

        // Ensure a title is selected
        if (!article.selectedTitle && article.titles.length > 0) {
          article.selectedTitle = article.titles[0];
        }

        setGeneratedArticle(article);
        toast.success('Article suggestions regenerated!', { id: 'regenerating' });
      })
      .catch(error => {
        console.error('Error regenerating article:', error);

        // Provide more specific error messages based on the error type
        let errorMessage = 'Failed to regenerate article suggestions. Please try again.';

        if (error.response) {
          // Handle specific HTTP error codes
          switch (error.response.status) {
            case 401:
              errorMessage = 'Authentication error. Please sign in again and retry.';
              break;
            case 403:
              errorMessage = 'You don\'t have permission to use this feature.';
              break;
            case 429:
              errorMessage = 'Too many requests. Please wait a moment and try again.';
              break;
            case 500:
              errorMessage = 'Server error. Our team has been notified.';
              break;
            case 404:
              errorMessage = 'API endpoint not found. Please check the configuration.';
              break;
            default:
              if (error.response.data?.error) {
                errorMessage = `Error: ${error.response.data.error}`;
              }
          }
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`;
        }

        toast.error(errorMessage, { id: 'regenerating', duration: 5000 });

        // Reset to planning form if we get a serious error
        if (error.response && [401, 403, 404].includes(error.response.status)) {
          setCurrentStep('planning');
          // Reset the generating state in the planning form
          if (dialogRef.current) {
            dialogRef.current.resetManualModeGeneratingState();
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Handle acceptance of the AI suggestions
  const handleAcceptSuggestions = (article: AIGeneratedArticle) => {
    if (!planningData) return;

    // Pass both the generated article and the form data to the parent component
    onComplete(article, planningData);
  };

  // This new handler will be called when the AI Chat mode completes.
  const handleAiChatComplete = (data: any) => {
    console.log("AI Chat completed. Bypassing suggestions and completing the wizard.", data);

    // The data from AIChatMode is the payload of the 'article' type response.
    // It includes: { title, purpose, category, tags, content, message }

    // 1. Create the AIGeneratedArticle object that the final onComplete expects.
    const article: AIGeneratedArticle = {
      titles: [data.title],
      selectedTitle: data.title,
      content: data.content,
      tags: data.tags || [],
      outline: [], // Outline is not generated in the direct-to-content flow.
    };

    // 2. Create a corresponding ArticlePlanningData object.
    const formData: ArticlePlanningData = {
      purpose: data.purpose || '',
      intent: data.purpose || '', // Intent can be derived from purpose.
      targetAudience: '', // Not provided in this flow.
      tone: '', // Not provided in this flow.
      category: data.category || '',
      tags: data.tags || [],
    };

    // 3. Call the final onComplete handler to close the modal and proceed to the editor.
    onComplete(article, formData);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        // Do nothing when clicking outside - modal can only be closed via buttons
        // This prevents accidental closing during article generation
      }}
      className="relative z-50"
    >
      {/* The backdrop, rendered as a fixed sibling to the panel container */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container to center the panel */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative mx-auto max-w-4xl w-full rounded-xl bg-white shadow-2xl overflow-hidden">
          {/* Modal header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <Dialog.Title className="text-xl font-bold text-gray-900">
                  AI Article Creation Wizard
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  {currentStep === 'planning'
                    ? mode === 'chat'
                      ? 'Create an article with our Content Copilot'
                      : 'Enter your article details manually'
                    : 'Review and customize AI-generated suggestions'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {showCreateFromBlank && (
                  <button
                    type="button"
                    onClick={handleCreateFromBlank}
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Create From Blank
                  </button>
                )}

                {currentStep === 'planning' && (
                  <button
                    onClick={() => setMode(mode === 'chat' ? 'manual' : 'chat')}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                    <span>{mode === 'chat' ? 'Switch to Manual Mode' : 'Switch to AI Chat'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 ml-2"
                >
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Modal content */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center z-10">
              <ArrowPathIcon className="h-12 w-12 text-blue-500 animate-spin mb-4" />
              <p className="text-lg text-gray-600">Generating article suggestions...</p>
              <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
            </div>
          )}
          {/* Modal content */}
          <div className={`bg-white ${mode === 'chat' && currentStep === 'planning' ? 'h-[70vh] flex flex-col' : 'p-6 max-h-[70vh] overflow-y-auto'}`}>
            {currentStep === 'planning' ? (
              <CreateArticleDialog
                ref={dialogRef}
                mode={mode}
                onComplete={handlePlanningComplete} // Used for manual mode
                onChatComplete={handleAiChatComplete} // Used for AI chat mode
                onClose={onClose}
                initialData={initialFormData}
                onStepChange={setManualModeStep}
                manualModeStep={manualModeStep}
              />
            ) : generatedArticle ? (
              <AIArticleSuggestions
                planningData={planningData!}
                generatedArticle={generatedArticle}
                isLoading={isLoading}
                onTitleSelect={handleTitleSelect}
                onOutlineEdit={handleOutlineEdit}
                onRegenerateRequest={handleRegenerateRequest}
                onAccept={handleAcceptSuggestions}
                onBack={() => setCurrentStep('planning')}
              />
            ) : null}
          </div>

          {/* Modal footer - Only show for Manual/Suggestions mode */}
          {(!((mode === 'chat' && currentStep === 'planning'))) && (
            <div className="flex justify-end items-center p-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center gap-4">
                {currentStep === 'planning' &&
                  mode === 'manual' &&
                  manualModeStep === 'style' && (
                    <button
                      type="button"
                      onClick={() => handleManualStepChange('back')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                    >
                      Back
                    </button>
                  )}

                {currentStep === 'planning' &&
                  mode === 'manual' &&
                  manualModeStep === 'purpose' && (
                    <button
                      type="button"
                      onClick={() => handleManualStepChange('next')}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                    >
                      Next
                    </button>
                  )}

                {currentStep === 'planning' &&
                  mode === 'manual' &&
                  manualModeStep === 'style' && (
                    <button
                      onClick={async () => {
                        const isValid = await dialogRef.current?.triggerValidation();
                        if (isValid) {
                          dialogRef.current?.submitForm();
                        }
                      }}
                      disabled={isLoading}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                    >
                      Generate Suggestions
                    </button>
                  )}

                {currentStep === 'suggestions' && (
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setCurrentStep('planning')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                    >
                      Back to Planning
                    </button>
                    <button
                      type="button"
                      onClick={handleRegenerateRequest}
                      disabled={isLoading}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Regenerate
                    </button>
                    <button
                      type="button"
                      onClick={() => generatedArticle && handleAcceptSuggestions(generatedArticle)}
                      disabled={isLoading}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      Accept & Continue to Editor
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
